import { useCallback, useEffect, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../api";
import { normalizeOrganizationPermissions } from "../../organization-permissions";
import type { AuthenticationConnection } from "../../components/integrations/AuthenticationConnectionsEditor";
import type { OrganizationNetworkPolicy } from "../../components/organization/OrganizationNetworkPolicyEditor";
import { t } from "../../i18n";
import { SettingsPage } from "../settings/ProfilePage";
import type { OrganizationCapabilities, User } from "@lumen/shared";
import { SiteOrganizationEditContent } from "./SiteOrganizationEditContent";
import { SiteOrganizationListContent } from "./SiteOrganizationListContent";
import {
  defaultOrganizationCapabilities,
  defaultOrganizationEntitlements,
} from "./SiteOrganizationsPage.utils";
import type {
  AccessRow,
  Audit,
  BillingInformation,
  CatalogResource,
  Forge,
  Member,
  OrganizationEntitlements,
  OrganizationIntegration,
  OrganizationPricing,
  OrganizationResource,
  OrganizationResources,
  OrganizationToolSetting,
  Row,
} from "./SiteOrganizationsPage.utils";
export function SiteOrganizationsWorkspace({
  permissions,
  user,
  mode,
  organizationId,
}: {
  permissions: string[];
  user: User;
  mode: "list" | "create" | "edit";
  organizationId?: string;
}) {
  const navigate = useNavigate();
  const params = useParams<{ tab?: string }>();
  const activeTab = params.tab ?? "overview";
  const [rows, setRows] = useState<Row[]>();
  const [forges, setForges] = useState<Forge[]>([]);
  const [forgeName, setForgeName] = useState("");
  const [forgeHost, setForgeHost] = useState("github.com");
  const [forgeKind, setForgeKind] = useState("github_app");
  const [forgeToken, setForgeToken] = useState("");
  const [forgeAppId, setForgeAppId] = useState("");
  const [forgeClientSecret, setForgeClientSecret] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Row["organization"] | null>(null);
  const [savedEditing, setSavedEditing] = useState<Row["organization"] | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [access, setAccess] = useState<AccessRow[]>([]);
  const [capabilities, setCapabilities] = useState<OrganizationCapabilities>(
    defaultOrganizationCapabilities,
  );
  const [networkPolicy, setNetworkPolicy] =
    useState<OrganizationNetworkPolicy | null>(null);
  const [savingNetworkPolicy, setSavingNetworkPolicy] = useState(false);
  const [entitlements, setEntitlements] = useState<OrganizationEntitlements>(
    defaultOrganizationEntitlements,
  );
  const [savingEntitlements, setSavingEntitlements] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [billing, setBilling] = useState<BillingInformation>({
    legalName: null,
    billingEmail: null,
    taxId: null,
    status: "unconfigured",
  });
  const [members, setMembers] = useState<Member[]>([]);
  const [audit, setAudit] = useState<Audit[]>([]);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [memberRole, setMemberRole] = useState("member");
  const [memberState, setMemberState] = useState("active");
  const [memberPermissions, setMemberPermissions] = useState<string[]>([]);
  const [defaultPermissions, setDefaultPermissions] = useState<string[]>([]);
  const [authConnections, setAuthConnections] = useState<
    AuthenticationConnection[]
  >([]);
  const [pricing, setPricing] = useState<OrganizationPricing | null>(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [resources, setResources] = useState<OrganizationResources>({
    skills: [],
    agents: [],
    tools: [],
    workspaces: [],
    repositories: [],
    automations: [],
  });
  const [resourceCatalog, setResourceCatalog] = useState<CatalogResource[]>([]);
  const [savingCatalogResource, setSavingCatalogResource] = useState<
    string | null
  >(null);
  const [toolSettings, setToolSettings] = useState<OrganizationToolSetting[]>(
    [],
  );
  const [openToolId, setOpenToolId] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<OrganizationIntegration[]>(
    [],
  );
  const [editingIntegrationId, setEditingIntegrationId] = useState<
    string | null
  >(null);
  const [editingIntegrationName, setEditingIntegrationName] = useState("");
  const [newOrganizationName, setNewOrganizationName] = useState("");
  const [newOrganizationSlug, setNewOrganizationSlug] = useState("");
  const [creatingOrganization, setCreatingOrganization] = useState(false);
  useEffect(() => {
    void Promise.all([
      api<Row[]>("/site/organizations"),
      permissions.includes("site:agent-workspaces:manage")
        ? api<Forge[]>("/site/git-forges")
        : Promise.resolve([]),
    ])
      .then(([nextRows, nextForges]) => {
        setRows(nextRows);
        setForges(nextForges);
      })
      .catch((cause) =>
        setError(
          cause instanceof Error
            ? cause.message
            : "Organizations could not be loaded",
        ),
      );
  }, [permissions]);
  const addForge = async () => {
    const forge = await api<Forge>("/site/git-forges", {
      method: "POST",
      body: JSON.stringify({
        name: forgeName,
        host: forgeHost,
        kind: forgeKind,
        appId: forgeAppId || null,
        credential:
          forgeToken || forgeClientSecret
            ? {
                ...(forgeToken ? { token: forgeToken } : {}),
                ...(forgeClientSecret
                  ? { clientSecret: forgeClientSecret }
                  : {}),
              }
            : null,
        enabled: true,
      }),
    });
    setForges((current) => [...current, forge]);
    setForgeName("");
    setForgeToken("");
    setForgeAppId("");
    setForgeClientSecret("");
  };
  const createOrganization = async () => {
    setCreatingOrganization(true);
    setError("");
    try {
      const organization = await api<Row["organization"]>(
        "/site/organizations",
        {
          method: "POST",
          body: JSON.stringify({
            name: newOrganizationName,
            slug: newOrganizationSlug,
            locale: "en",
            currency: "EUR",
            timezone: "UTC",
          }),
        },
      );
      setRows((current) => [
        ...(current ?? []),
        {
          organization,
          memberCount: 1,
          promptCount: 0,
          billingStatus: null,
          subscriptionState: null,
          securityStatus: "attention",
        },
      ]);
      setNewOrganizationName("");
      setNewOrganizationSlug("");
      navigate(`/settings/organizations/${organization.id}/overview`);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization could not be created",
      );
    } finally {
      setCreatingOrganization(false);
    }
  };
  const saveOrganization = async () => {
    if (!editing) return;
    setSaving(true);
    setError("");
    try {
      const updated = await api<Row["organization"]>(
        `/site/organizations/${editing.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name: editing.name,
            slug: editing.slug,
            state: editing.state,
            locale: editing.locale,
            currency: editing.currency,
            timezone: editing.timezone,
            retentionDays: editing.retentionDays,
          }),
        },
      );
      setRows((current) =>
        current?.map((row) =>
          row.organization.id === updated.id
            ? { ...row, organization: { ...row.organization, ...updated } }
            : row,
        ),
      );
      setEditing(updated);
      setSavedEditing(updated);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization could not be saved",
      );
    } finally {
      setSaving(false);
    }
  };
  const openOrganization = useCallback(
    async (organization: Row["organization"]) => {
      setEditing({ ...organization });
      setSavedEditing({ ...organization });
      setError("");
      try {
        const [
          nextAccess,
          nextPricing,
          capabilities,
          billingInformation,
          nextMembers,
          nextAuthConnections,
          nextDefaults,
          nextResources,
          nextResourceCatalog,
          nextToolSettings,
          nextNetworkPolicy,
          nextAudit,
          nextEntitlements,
        ] = await Promise.all([
          permissions.includes("site:organization-access:manage")
            ? api<AccessRow[]>(`/site/organizations/${organization.id}/access`)
            : Promise.resolve([]),
          permissions.includes("site:pricing:manage")
            ? api<OrganizationPricing>(
                `/site/organizations/${organization.id}/pricing`,
              )
            : Promise.resolve(null),
          permissions.includes("site:organization-access:manage")
            ? api<OrganizationCapabilities>(
                `/site/organizations/${organization.id}/capabilities`,
              )
            : Promise.resolve({
                personalProviders: false,
                organizationProviders: false,
                personalWorkspaces: false,
                sshWorkspaces: false,
                unrestrictedSshHosts: false,
                privateNetwork: false,
                customProviderEndpoints: false,
                unrestrictedProviderHosts: false,
                unrestrictedOutboundHosts: false,
                customWebhooks: false,
                customerManagedConnectors: false,
                allowInternetEgress: false,
              }),
          permissions.includes("site:organization-billing:manage")
            ? api<BillingInformation>(
                `/site/organizations/${organization.id}/billing-information`,
              )
            : Promise.resolve({
                legalName: null,
                billingEmail: null,
                taxId: null,
                status: "unavailable",
              }),
          permissions.includes("site:organization-users:manage")
            ? api<Member[]>(`/site/organizations/${organization.id}/members`)
            : Promise.resolve([]),
          permissions.includes("site:organization-auth:manage")
            ? api<AuthenticationConnection[]>(
                `/site/organizations/${organization.id}/auth-connections`,
              )
            : Promise.resolve([]),
          permissions.includes("site:organization-users:manage")
            ? api<{ permissions: string[] }>(
                `/site/organizations/${organization.id}/default-permissions`,
              )
            : Promise.resolve({ permissions: [] }),
          permissions.includes("site:organization-access:manage")
            ? api<OrganizationResources>(
                `/site/organizations/${organization.id}/resources`,
              )
            : Promise.resolve({
                skills: [],
                agents: [],
                tools: [],
                workspaces: [],
                repositories: [],
                automations: [],
              }),
          permissions.includes("site:organization-access:manage")
            ? api<CatalogResource[]>(
                `/site/organizations/${organization.id}/resource-catalog`,
              )
            : Promise.resolve([]),
          permissions.includes("site:organization-access:manage")
            ? api<OrganizationToolSetting[]>(
                `/site/organizations/${organization.id}/tool-settings`,
              )
            : Promise.resolve([]),
          permissions.includes("site:organization-access:manage")
            ? api<OrganizationNetworkPolicy>(
                `/site/organizations/${organization.id}/network-policy`,
              )
            : Promise.resolve(null),
          permissions.includes("site:organizations:read")
            ? api<Audit[]>(
                `/site/organizations/${organization.id}/audit-events`,
              )
            : Promise.resolve([]),
          permissions.includes("site:organization-access:manage")
            ? api<OrganizationEntitlements>(
                `/site/organizations/${organization.id}/entitlements`,
              )
            : Promise.resolve(null),
        ]);
        setAccess(nextAccess);
        setCapabilities(capabilities);
        setBilling(billingInformation);
        setMembers(nextMembers);
        setAuthConnections(nextAuthConnections);
        setDefaultPermissions(
          normalizeOrganizationPermissions(nextDefaults.permissions),
        );
        setResources(nextResources);
        setResourceCatalog(nextResourceCatalog);
        setToolSettings(nextToolSettings);
        setOpenToolId(null);
        setNetworkPolicy(nextNetworkPolicy);
        setAudit(nextAudit);
        if (nextEntitlements) setEntitlements(nextEntitlements);
        setPricing(nextPricing);
        if (permissions.includes("site:organization-access:manage")) {
          void api<OrganizationIntegration[]>(
            `/site/organizations/${organization.id}/integrations`,
          )
            .then(setIntegrations)
            .catch(() => setIntegrations([]));
        }
      } catch (cause) {
        setError(
          cause instanceof Error
            ? cause.message
            : "Organization access settings could not be loaded",
        );
      }
    },
    [permissions],
  );
  useEffect(() => {
    if (mode !== "edit" || !organizationId || editing || !rows) return;
    const organization = rows.find(
      (row) => row.organization.id === organizationId,
    )?.organization;
    if (organization) void openOrganization(organization);
  }, [editing, mode, openOrganization, organizationId, rows]);
  const setIntegrationEnabled = async (
    integration: OrganizationIntegration,
    enabled: boolean,
  ) => {
    if (!editing) return;
    const updated = await api<OrganizationIntegration>(
      `/site/organizations/${editing.id}/integrations/${integration.id}`,
      { method: "PATCH", body: JSON.stringify({ enabled }) },
    );
    setIntegrations((current) =>
      current.map((item) => (item.id === updated.id ? updated : item)),
    );
  };
  const renameIntegration = async () => {
    if (!editing || !editingIntegrationId || !editingIntegrationName.trim())
      return;
    const updated = await api<OrganizationIntegration>(
      `/site/organizations/${editing.id}/integrations/${editingIntegrationId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ name: editingIntegrationName.trim() }),
      },
    );
    setIntegrations((current) =>
      current.map((item) => (item.id === updated.id ? updated : item)),
    );
    setEditingIntegrationId(null);
  };
  const deleteIntegration = async (integration: OrganizationIntegration) => {
    if (!editing || !window.confirm(t("Delete this organization integration?")))
      return;
    await api(
      `/site/organizations/${editing.id}/integrations/${integration.id}`,
      { method: "DELETE" },
    );
    setIntegrations((current) =>
      current.filter((item) => item.id !== integration.id),
    );
  };
  const setProviderAccess = async (providerId: string, enabled: boolean) => {
    if (!editing) return;
    await api(
      `/site/organizations/${editing.id}/providers/${providerId}/access`,
      { method: "PUT", body: JSON.stringify({ enabled }) },
    );
    setAccess((current) =>
      current.map((row) =>
        row.providerId === providerId
          ? { ...row, entitlementEnabled: enabled }
          : row,
      ),
    );
  };
  const setModelAccess = async (modelId: string, enabled: boolean) => {
    if (!editing) return;
    await api(
      `/site/organizations/${editing.id}/resource-entitlements/model/${modelId}`,
      { method: "PUT", body: JSON.stringify({ enabled }) },
    );
    setAccess((current) =>
      current.map((row) =>
        row.modelId === modelId ? { ...row, entitlementEnabled: enabled } : row,
      ),
    );
  };
  const setModelPolicy = async (
    modelId: string,
    defaultEffect: "allow" | "deny",
  ) => {
    if (!editing) return;
    await api(
      `/site/organizations/${editing.id}/resource-policies/model/${modelId}`,
      { method: "PUT", body: JSON.stringify({ defaultEffect }) },
    );
    setAccess((current) =>
      current.map((row) =>
        row.modelId === modelId ? { ...row, defaultEffect } : row,
      ),
    );
  };
  const setOrganizationCapabilities = async (
    next: OrganizationCapabilities,
  ) => {
    if (!editing) return;
    await api(`/site/organizations/${editing.id}/capabilities`, {
      method: "PUT",
      body: JSON.stringify(next),
    });
    setCapabilities(next);
  };
  const saveNetworkPolicy = async (
    outboundHosts: string[] | null,
    providerHosts: string[] | null,
  ) => {
    if (!editing) return;
    setSavingNetworkPolicy(true);
    try {
      const updated = await api<OrganizationNetworkPolicy>(
        `/site/organizations/${editing.id}/network-policy`,
        {
          method: "PUT",
          body: JSON.stringify({ outboundHosts, providerHosts }),
        },
      );
      setNetworkPolicy((current) =>
        current
          ? {
              ...current,
              outboundHosts: updated.outboundHosts,
              providerHosts: updated.providerHosts,
              effectiveOutboundHosts: updated.effectiveOutboundHosts,
              effectiveProviderHosts: updated.effectiveProviderHosts,
            }
          : current,
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization network policy could not be saved",
      );
    } finally {
      setSavingNetworkPolicy(false);
    }
  };
  const saveEntitlements = async () => {
    if (!editing) return;
    setSavingEntitlements(true);
    try {
      const updated = await api<OrganizationEntitlements>(
        `/site/organizations/${editing.id}/entitlements`,
        { method: "PUT", body: JSON.stringify(entitlements) },
      );
      setEntitlements(updated);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization limits could not be saved",
      );
    } finally {
      setSavingEntitlements(false);
    }
  };
  const setExecutionEnabled = async (enabled: boolean) => {
    if (!editing) return;
    setSavingEntitlements(true);
    try {
      const updated = await api<{ executionEnabled: boolean }>(
        `/site/organizations/${editing.id}/execution`,
        { method: "PATCH", body: JSON.stringify({ enabled }) },
      );
      setEntitlements((current) => ({
        ...current,
        executionEnabled: updated.executionEnabled,
      }));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization execution could not be changed",
      );
    } finally {
      setSavingEntitlements(false);
    }
  };
  const setAllToolsEnabled = async (enabled: boolean) => {
    if (!editing) return;
    setSavingEntitlements(true);
    try {
      const updated = await api<{ allToolsEnabled: boolean }>(
        `/site/organizations/${editing.id}/all-tools`,
        { method: "PATCH", body: JSON.stringify({ enabled }) },
      );
      setEntitlements((current) => ({
        ...current,
        allToolsEnabled: updated.allToolsEnabled,
      }));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization tool access could not be changed",
      );
    } finally {
      setSavingEntitlements(false);
    }
  };
  const changeLifecycle = async (action: "schedule" | "recover") => {
    if (!editing) return;
    if (
      action === "schedule" &&
      !window.confirm(
        t(
          "Schedule this organization for deletion after its retention period?",
        ),
      )
    )
      return;
    setLifecycleBusy(true);
    try {
      const updated = await api<{
        state: Row["organization"]["state"];
        deletionScheduledAt: string | null;
      }>(`/site/organizations/${editing.id}/offboarding`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      const nextEditing = { ...editing, state: updated.state };
      setEditing(nextEditing);
      setSavedEditing(nextEditing);
      setRows((current) =>
        current?.map((row) =>
          row.organization.id === editing.id
            ? {
                ...row,
                organization: { ...row.organization, state: updated.state },
              }
            : row,
        ),
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization lifecycle could not be changed",
      );
    } finally {
      setLifecycleBusy(false);
    }
  };
  const saveBillingInformation = async () => {
    if (!editing || !billing.legalName || !billing.billingEmail) return;
    const updated = await api<BillingInformation>(
      `/site/organizations/${editing.id}/billing-information`,
      {
        method: "PATCH",
        body: JSON.stringify({
          legalName: billing.legalName,
          billingEmail: billing.billingEmail,
          taxId: billing.taxId || null,
        }),
      },
    );
    setBilling(updated);
  };
  const editMember = (member: Member) => {
    setEditingMember(member);
    setMemberRole(member.membership.role);
    setMemberState(member.membership.state);
    setMemberPermissions(
      normalizeOrganizationPermissions(member.permissions ?? []),
    );
    window.requestAnimationFrame(() =>
      document
        .getElementById("site-member-permissions-editor")
        ?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };
  const saveMember = async () => {
    if (!editing || !editingMember) return;
    await api(
      `/site/organizations/${editing.id}/members/${editingMember.user.id}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          role: memberRole,
          state: memberState,
          permissions: normalizeOrganizationPermissions(memberPermissions),
        }),
      },
    );
    setMembers(
      await api<Member[]>(`/site/organizations/${editing.id}/members`),
    );
    setEditingMember(null);
  };
  const inviteMember = async (input: {
    name: string;
    email: string;
    role: string;
  }) => {
    if (!editing) return;
    await api(`/site/organizations/${editing.id}/members/invite`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    setMembers(
      await api<Member[]>(`/site/organizations/${editing.id}/members`),
    );
  };
  const saveDefaultPermissions = async () => {
    if (!editing) return;
    await api(`/site/organizations/${editing.id}/default-permissions`, {
      method: "PUT",
      body: JSON.stringify({
        permissions: normalizeOrganizationPermissions(defaultPermissions),
      }),
    });
  };
  const updateNativePolicy = async (
    connection: AuthenticationConnection,
    policy: NonNullable<AuthenticationConnection["nativePolicy"]>,
  ) => {
    if (!editing) return;
    await api(
      `/site/organizations/${editing.id}/auth-connections/${connection.id}/native-policy`,
      { method: "PATCH", body: JSON.stringify(policy) },
    );
    setAuthConnections((current) =>
      current.map((item) =>
        item.id === connection.id ? { ...item, nativePolicy: policy } : item,
      ),
    );
  };
  const createAuthenticationConnection = async (
    input: Record<string, unknown>,
  ) => {
    if (!editing) return;
    const created = await api<AuthenticationConnection>(
      `/site/organizations/${editing.id}/auth-connections`,
      { method: "POST", body: JSON.stringify(input) },
    );
    setAuthConnections((current) => [...current, created]);
  };
  const savePricing = async () => {
    if (!editing || !pricing) return;
    setSavingPricing(true);
    try {
      setPricing(
        await api<OrganizationPricing>(
          `/site/organizations/${editing.id}/pricing`,
          {
            method: "PUT",
            body: JSON.stringify({
              currency: pricing.currency,
              prices: pricing.prices,
            }),
          },
        ),
      );
    } finally {
      setSavingPricing(false);
    }
  };
  const setResourceEnabled = async (
    type: keyof OrganizationResources,
    resource: OrganizationResource,
    enabled: boolean,
  ) => {
    if (!editing) return;
    const singular = type === "repositories" ? "repository" : type.slice(0, -1);
    await api(
      `/site/organizations/${editing.id}/resources/${singular}/${resource.id}`,
      { method: "PATCH", body: JSON.stringify({ enabled }) },
    );
    setResources((current) => ({
      ...current,
      [type]: current[type].map((item) =>
        item.id === resource.id ? { ...item, enabled } : item,
      ),
    }));
  };
  const setCatalogAccess = async (
    resource: CatalogResource,
    enabled: boolean,
  ) => {
    if (!editing) return;
    const resourceKey = `${resource.resourceType}:${resource.id}`;
    setSavingCatalogResource(resourceKey);
    setError("");
    try {
      await api(
        `/site/organizations/${editing.id}/resource-entitlements/${resource.resourceType}/${resource.id}`,
        { method: "PUT", body: JSON.stringify({ enabled }) },
      );
      setResourceCatalog((current) =>
        current.map((item) =>
          item.resourceType === resource.resourceType && item.id === resource.id
            ? { ...item, entitlementEnabled: enabled }
            : item,
        ),
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Platform resource access could not be updated",
      );
    } finally {
      setSavingCatalogResource(null);
    }
  };
  const saveToolSettings = async (
    tool: OrganizationToolSetting,
    config: Record<string, unknown>,
  ) => {
    if (!editing) return;
    try {
      await api(`/site/organizations/${editing.id}/tool-settings/${tool.id}`, {
        method: "PUT",
        body: JSON.stringify(config),
      });
      setToolSettings((current) =>
        current.map((item) =>
          item.id === tool.id ? { ...item, config, configured: true } : item,
        ),
      );
      setOpenToolId(null);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization tool settings could not be saved",
      );
    }
  };
  const resetToolSettings = async (tool: OrganizationToolSetting) => {
    if (!editing) return;
    try {
      await api(`/site/organizations/${editing.id}/tool-settings/${tool.id}`, {
        method: "DELETE",
      });
      setToolSettings((current) =>
        current.map((item) =>
          item.id === tool.id
            ? { ...item, config: {}, configured: false }
            : item,
        ),
      );
      setOpenToolId(null);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization tool settings could not be reset",
      );
    }
  };
  const openTool = openToolId
    ? (toolSettings.find((tool) => tool.id === openToolId) ?? null)
    : null;
  return (
    <SettingsPage
      title={
        mode === "create"
          ? t("Create organization")
          : mode === "edit"
            ? (editing?.name ?? t("Organization"))
            : t("Organizations")
      }
      description={
        mode === "edit"
          ? t("Manage this organization's configuration and resources.")
          : t(
              "Site-wide organization lifecycle, plan, usage, and payment status.",
            )
      }
    >
      {error && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {!rows && !error && (
        <div className="grid min-h-64 place-items-center">
          <LoaderCircle className="animate-spin text-stone-400" />
        </div>
      )}
      {mode === "create" &&
        permissions.includes("site:organizations:manage") && (
          <section className="panel mb-6 p-5">
            <h2 className="font-bold">{t("Create organization")}</h2>
            <p className="mt-1 text-sm text-stone-500">
              {t(
                "Create a new organization and configure its members, access, authentication, and billing.",
              )}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input
                className="field"
                placeholder={t("Organization name")}
                value={newOrganizationName}
                onChange={(event) => {
                  const name = event.target.value;
                  setNewOrganizationName(name);
                  setNewOrganizationSlug((current) =>
                    current
                      ? current
                      : name
                          .toLowerCase()
                          .trim()
                          .replace(/[^a-z0-9]+/g, "-")
                          .replace(/^-|-$/g, ""),
                  );
                }}
              />
              <input
                className="field"
                placeholder={t("Organization slug")}
                value={newOrganizationSlug}
                onChange={(event) =>
                  setNewOrganizationSlug(event.target.value.toLowerCase())
                }
              />
            </div>
            <div className="mt-3 flex justify-end">
              <button
                className="button-primary"
                disabled={
                  creatingOrganization ||
                  !newOrganizationName ||
                  !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(newOrganizationSlug)
                }
                onClick={() => void createOrganization()}
              >
                {creatingOrganization && (
                  <LoaderCircle className="animate-spin" size={15} />
                )}
                {t("Create organization")}
              </button>
            </div>
          </section>
        )}
      {mode === "edit" && editing && (
        <SiteOrganizationEditContent
          activeTab={activeTab}
          permissions={permissions}
          editing={editing}
          savedEditing={savedEditing}
          navigate={navigate}
          saving={saving}
          setEditing={setEditing}
          saveOrganization={saveOrganization}
          members={members}
          editMember={editMember}
          inviteMember={inviteMember}
          defaultPermissions={defaultPermissions}
          setDefaultPermissions={setDefaultPermissions}
          capabilities={capabilities}
          saveDefaultPermissions={saveDefaultPermissions}
          editingMember={editingMember}
          setEditingMember={setEditingMember}
          memberRole={memberRole}
          setMemberRole={setMemberRole}
          memberState={memberState}
          setMemberState={setMemberState}
          memberPermissions={memberPermissions}
          setMemberPermissions={setMemberPermissions}
          saveMember={saveMember}
          authConnections={authConnections}
          createAuthenticationConnection={createAuthenticationConnection}
          updateNativePolicy={updateNativePolicy}
          networkPolicy={networkPolicy}
          savingNetworkPolicy={savingNetworkPolicy}
          saveNetworkPolicy={saveNetworkPolicy}
          setNetworkPolicy={setNetworkPolicy}
          integrations={integrations}
          editingIntegrationId={editingIntegrationId}
          setEditingIntegrationId={setEditingIntegrationId}
          editingIntegrationName={editingIntegrationName}
          setEditingIntegrationName={setEditingIntegrationName}
          renameIntegration={renameIntegration}
          deleteIntegration={deleteIntegration}
          setIntegrationEnabled={setIntegrationEnabled}
          billing={billing}
          setBilling={setBilling}
          saveBillingInformation={saveBillingInformation}
          pricing={pricing}
          savingPricing={savingPricing}
          setPricing={setPricing}
          savePricing={savePricing}
          access={access}
          setProviderAccess={setProviderAccess}
          setModelAccess={setModelAccess}
          setModelPolicy={setModelPolicy}
          resourceCatalog={resourceCatalog}
          entitlements={entitlements}
          savingEntitlements={savingEntitlements}
          setAllToolsEnabled={setAllToolsEnabled}
          savingCatalogResource={savingCatalogResource}
          setCatalogAccess={setCatalogAccess}
          toolSettings={toolSettings}
          openTool={openTool}
          setOpenToolId={setOpenToolId}
          setToolSettings={setToolSettings}
          resetToolSettings={resetToolSettings}
          saveToolSettings={saveToolSettings}
          resources={resources}
          user={user}
          setResourceEnabled={setResourceEnabled}
          lifecycleBusy={lifecycleBusy}
          changeLifecycle={changeLifecycle}
          audit={audit}
          setExecutionEnabled={setExecutionEnabled}
          setOrganizationCapabilities={setOrganizationCapabilities}
          setEntitlements={setEntitlements}
          saveEntitlements={saveEntitlements}
        />
      )}
      <SiteOrganizationListContent
        mode={mode}
        permissions={permissions}
        rows={rows}
        forges={forges}
        forgeName={forgeName}
        setForgeName={setForgeName}
        forgeHost={forgeHost}
        setForgeHost={setForgeHost}
        forgeKind={forgeKind}
        setForgeKind={setForgeKind}
        forgeToken={forgeToken}
        setForgeToken={setForgeToken}
        forgeAppId={forgeAppId}
        setForgeAppId={setForgeAppId}
        forgeClientSecret={forgeClientSecret}
        setForgeClientSecret={setForgeClientSecret}
        addForge={addForge}
      />
    </SettingsPage>
  );
}
